import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface DeliveryModel {
  id: number;
  model_id?: number;
  model_name: string;
  delivery_type: number;
  hours?: number | null;
  days?: number | null;
  display_text: string;
  status: number;
  is_default: number;
  is_restricted: number;
  delivery_charge: string;
  cutoff_time?: string | null;
  window_start?: string | null;
  window_end?: string | null;
  created_at?: string;
  createddt?: string;
  updated_at?: string;
  updateddt?: string;
}

export interface DeliveryModelResponse {
  error: boolean;
  message: string;
  result?: DeliveryModel[];
  data?: DeliveryModel[];
}

export interface DeliveryModelRequest {
  model_name: string;
  delivery_type: number;
  hours?: number;
  days?: number;
  display_text: string;
  status?: number;
  is_default?: number;
  is_restricted?: number;
  delivery_charge: string;
  /** "HH:MM" daily order cut-off. '' clears it; undefined leaves it unchanged. */
  cutoff_time?: string;
  /** Delivery window "HH:MM" from/to. Both '' clears; undefined leaves unchanged. */
  window_start?: string;
  window_end?: string;
}

export interface UpdateDeliveryModelRequest extends DeliveryModelRequest {
  model_id: number;
}

export interface DeleteDeliveryModelRequest {
  model_id: number;
}

export const deliveryApi = createApi({
  reducerPath: 'deliveryApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['DeliveryModels'],
  endpoints: (build) => ({
    listDeliveryModels: build.query<DeliveryModelResponse, void>({
      query: () => ({
        url: 'globalsettings/listDeliveryModel',
        method: 'POST',
        body: new URLSearchParams(),
      }),
      providesTags: ['DeliveryModels'],
      transformResponse: (response: any) => {
        console.log('List API Response:', response);
        
        let deliveryModels = [];
        
        if (Array.isArray(response.result)) {
          deliveryModels = response.result;
        } else if (Array.isArray(response.data)) {
          deliveryModels = response.data;
        } else if (Array.isArray(response)) {
          deliveryModels = response;
        }
        
        const formattedModels = deliveryModels.map((model: any) => ({
          ...model,
          id: model.id || model.model_id,
          model_id: model.model_id || model.id,
          hours: model.hours === null ? 0 : (model.hours || 0),
          days: model.days === null ? 0 : (model.days || 0),
          delivery_charge: model.delivery_charge || 'FREE',
          status: model.status || 1,
          is_default: model.is_default || 0,
          is_restricted: model.is_restricted || 1,
          createddt: model.createddt || model.created_at,
          updateddt: model.updateddt || model.updated_at,
        }));
        
        return {
          error: response.error || false,
          message: response.message || '',
          result: formattedModels,
          data: formattedModels,
        };
      },
    }),

    addDeliveryModel: build.mutation<DeliveryModelResponse, DeliveryModelRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('model_name', data.model_name.trim());
        formData.append('delivery_type', data.delivery_type.toString());
        
        if (data.delivery_type === 0 && data.hours != null) {
          formData.append('hours', data.hours.toString());
        } else if (data.delivery_type === 1 && data.days != null) {
          formData.append('days', data.days.toString());
        }
        
        formData.append('display_text', data.display_text.trim());
        formData.append('status', (data.status || 1).toString());
        formData.append('is_default', (data.is_default || 0).toString());
        formData.append('is_restricted', (data.is_restricted || 1).toString());
        formData.append('delivery_charge', data.delivery_charge?.trim() || 'FREE');
        if (data.cutoff_time !== undefined) formData.append('cutoff_time', data.cutoff_time);
        if (data.window_start !== undefined) formData.append('window_start', data.window_start);
        if (data.window_end !== undefined) formData.append('window_end', data.window_end);

        console.log('Add Delivery Request:', {
          model_name: data.model_name.trim(),
          delivery_type: data.delivery_type,
          hours: data.hours,
          days: data.days,
          display_text: data.display_text.trim(),
          status: data.status || 1,
          is_default: data.is_default || 0,
          is_restricted: data.is_restricted || 1,
          delivery_charge: data.delivery_charge?.trim() || 'FREE',
        });

        return {
          url: 'globalsettings/newDeliveryModel',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['DeliveryModels'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          console.log('Add Delivery Response:', data);
          dispatch(deliveryApi.util.invalidateTags(['DeliveryModels']));
        } catch (error) {
          console.error('Add delivery failed:', error);
        }
      },
    }),

    updateDeliveryModel: build.mutation<DeliveryModelResponse, UpdateDeliveryModelRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('model_id', data.model_id.toString());
        formData.append('model_name', data.model_name.trim());
        formData.append('delivery_type', data.delivery_type.toString());
        
        if (data.delivery_type === 0 && data.hours != null) {
          formData.append('hours', data.hours.toString());
        } else if (data.delivery_type === 1 && data.days != null) {
          formData.append('days', data.days.toString());
        }
        
        formData.append('display_text', data.display_text.trim());
        formData.append('status', (data.status || 1).toString());
        formData.append('is_default', (data.is_default || 0).toString());
        formData.append('is_restricted', (data.is_restricted || 1).toString());
        formData.append('delivery_charge', data.delivery_charge?.trim() || 'FREE');
        if (data.cutoff_time !== undefined) formData.append('cutoff_time', data.cutoff_time);
        if (data.window_start !== undefined) formData.append('window_start', data.window_start);
        if (data.window_end !== undefined) formData.append('window_end', data.window_end);

        console.log('Update Delivery Request:', {
          model_id: data.model_id,
          model_name: data.model_name.trim(),
          delivery_type: data.delivery_type,
          hours: data.hours,
          days: data.days,
          display_text: data.display_text.trim(),
          status: data.status || 1,
          is_default: data.is_default || 0,
          is_restricted: data.is_restricted || 1,
          delivery_charge: data.delivery_charge?.trim() || 'FREE',
        });

        return {
          url: 'globalsettings/updateDeliveryModel',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['DeliveryModels'],
      async onQueryStarted(updateData, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(deliveryApi.util.invalidateTags(['DeliveryModels']));
        } catch (error) {
          console.error('Update failed:', error);
        }
      },
    }),

    deleteDeliveryModel: build.mutation<DeliveryModelResponse, DeleteDeliveryModelRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('model_id', data.model_id.toString());

        return {
          url: 'globalsettings/deleteDeliveryModel',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['DeliveryModels'],
    }),
  }),
});

export const {
  useListDeliveryModelsQuery,
  useAddDeliveryModelMutation,
  useUpdateDeliveryModelMutation,
  useDeleteDeliveryModelMutation,
} = deliveryApi;